import AccessTime from '@mui/icons-material/AccessTime';
import CalendarToday from '@mui/icons-material/CalendarToday';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { STRINGS } from '../../../constants';
import { clampIsoDateToRange } from '../../../utils/dateAndTime';
import { formatCollectionDateDisplay } from '../helpers';

// Full-size overlay so iOS Safari gets a direct tap on a real input.
// Chrome often ignores clicks on opacity:0 date/time fields, so we also call
// showPicker() from this input's own click (same user gesture).
const overlayNativeInputStyle = {
  position: 'absolute',
  inset: 0,
  opacity: 0.01,
  width: '100%',
  height: '100%',
  margin: 0,
  padding: 0,
  border: 'none',
  cursor: 'pointer',
  zIndex: 1,
};

function openNativePicker(event) {
  const input = event.currentTarget;
  if (typeof input.showPicker !== 'function') {
    return;
  }
  try {
    input.showPicker();
  } catch {
    // NotAllowedError / unsupported — iOS still opens from the direct tap.
  }
}

export function CitysprintCollectionSchedulePicker({
  enabled,
  collectionDate,
  timeHour,
  timeMinute,
  todayIso,
  collectionDateMaxIso,
  onChangeDate,
  onChangeTime,
  collectionScheduleInPast,
  quoteValidityText,
  onGetQuote,
  getQuoteDisabled,
}) {
  const timeDisplay = `${String(timeHour).padStart(2, '0')}:${String(
    timeMinute
  ).padStart(2, '0')}`;
  const dateFieldValue = enabled
    ? formatCollectionDateDisplay(collectionDate, todayIso)
    : '--';
  const timeFieldValue = enabled ? timeDisplay : '--';

  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        opacity: enabled ? 1 : 0.4,
        pointerEvents: enabled ? 'auto' : 'none',
      }}
      role='region'
      aria-labelledby='citysprint-booking-mode-title'
    >
      <Typography
        variant='h3'
        component='h2'
        id='citysprint-booking-mode-title'
        sx={{ mb: 2 }}
      >
        When would you like us to collect your parcel?
      </Typography>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
          <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
            <TextField
              required
              id='collection-date-display'
              name='collectionDateDisplay'
              label='Collection date'
              value={dateFieldValue}
              slotProps={{ inputLabel: { shrink: true } }}
              inputProps={{
                readOnly: true,
                tabIndex: -1,
                'aria-hidden': true,
              }}
              sx={{
                '& .MuiOutlinedInput-input': { cursor: 'pointer' },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      aria-hidden
                      tabIndex={-1}
                      edge='end'
                      disabled={!enabled}
                      size='small'
                    >
                      <CalendarToday />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
              disabled={!enabled}
            />
            <input
              type='date'
              min={todayIso}
              max={collectionDateMaxIso}
              value={collectionDate ?? ''}
              onClick={openNativePicker}
              onChange={e =>
                onChangeDate(
                  clampIsoDateToRange(
                    e.target.value,
                    todayIso,
                    collectionDateMaxIso
                  )
                )
              }
              aria-label='Collection date'
              disabled={!enabled}
              style={overlayNativeInputStyle}
            />
          </Box>
          <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
            <TextField
              required
              id='collection-time'
              name='collectionTime'
              label='Collection time'
              value={timeFieldValue}
              slotProps={{ inputLabel: { shrink: true } }}
              inputProps={{
                readOnly: true,
                tabIndex: -1,
                'aria-hidden': true,
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiOutlinedInput-input': { cursor: 'pointer' },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      aria-hidden
                      tabIndex={-1}
                      edge='end'
                      disabled={!enabled}
                      size='small'
                    >
                      <AccessTime />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
              disabled={!enabled}
            />
            <input
              type='time'
              step={60}
              value={timeDisplay}
              onClick={openNativePicker}
              onChange={e => {
                const v = e.target.value;
                if (!v) {
                  return;
                }
                const [h, m] = v.split(':');
                onChangeTime({
                  hour: (h ?? '00').padStart(2, '0'),
                  minute: (m ?? '00').padStart(2, '0'),
                });
              }}
              aria-label='Collection time'
              disabled={!enabled}
              style={overlayNativeInputStyle}
            />
          </Box>
        </Stack>
        {collectionScheduleInPast && enabled ? (
          <Typography variant='body2' color='error' role='alert'>
            {STRINGS.COLLECTION_DATE_TIME_IN_THE_PAST}
          </Typography>
        ) : null}
        {!collectionScheduleInPast && quoteValidityText ? (
          <Typography variant='body2' color='text.secondary'>
            {quoteValidityText}
          </Typography>
        ) : null}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type='button'
            variant='outlined'
            color='primary'
            disabled={getQuoteDisabled}
            onClick={onGetQuote}
            sx={{
              textTransform: 'uppercase',
              fontWeight: 500,
              letterSpacing: 0.4,
            }}
          >
            {STRINGS.GET_QUOTE}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}
